import React, { FC, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { mutate } from 'swr';
import deleteSurvey from '../../../api/survey/deleteSurvey';
import syncSurveyDatas from '../../../api/survey/syncSurveyDatas';
import { TenantContext } from '../../../contexts/tenantContext';
import useFetcher from '../../../utils/hooks/useFetcher';
import CreateSurvey from '../../Modals/AddSurveyModal/createSurvey';
import DeleteContentModal from '../../Modals/DeleteModal/deleteModal';
import { Header, HEADER_TYPE } from '../../Table/type';
import VirtualizedTable from '../../Table/virtualizedTable';
import AddSurveysAction from '../addSurveyAction';
import { HeaderSurveysListStyled, SurveysListActionsHeaderStyled } from '../styled';
import { SurveyForConfigurationProps, SurveyStatusToLabel } from '../surveyInterface';

const SurveysList: FC = () => {
    const fetcher = useFetcher();
    const { t } = useTranslation('settings');
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const surveysUrl = `${process.env.REACT_APP_API_URL}/configuration/surveys`;
    const { data: fetchedSurveys } = useSWR(surveysUrl, fetcher);
    const [openCreateSurveyModal, setOpenCreateSurveyModal] = useState<boolean>(false);
    const [headers, setHeaders] = useState<Header[]>([]);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [surveyIdToDelete, setSurveyIdToDelete] = useState<number | null>(null);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [openReloadModal, setOpenReloadModal] = useState<boolean>(false);
    const [surveyIdToReload, setSurveyIdToReload] = useState<number | null>(null);
    const [reloadModalTitle, setReloadModalTitle] = useState<string>('');
    const [initialSurveyReload, setInitialSurveyReload] = useState<boolean>(false);
    const [surveyToEdit, setSurveyToEdit] = useState<SurveyForConfigurationProps | undefined>(undefined);

    const refreshMutate = () => {
        mutate(surveysUrl);
    };

    function listToUnique(items: Array<string>) {
        return Array.from(new Set(items)).sort((a, b) => a.localeCompare(b));
    }
    const handleDeleteRow = (row: any) => {
        setModalTitle(t('surveys.deleteSurveyModal', { surveyName: row.name }));
        setSurveyIdToDelete(row.surveyid);
        setOpenDeleteModal(true);
    };

    const handleDeleteRowConfirm = () => {
        if (surveyIdToDelete === null) { return; }
        deleteSurvey(surveyIdToDelete, t('surveys.modal.successDeleteSurvey'), t('surveys.modal.errorDeleteSurvey'), refreshMutate, selectedSchema || '');
        setSurveyIdToDelete(null);
        setOpenDeleteModal(false);
    };

    const handleReloadRow = (row: any) => {
        setReloadModalTitle(t('surveys.reloadSurveyModal', { surveyName: row.name }));
        setSurveyIdToReload(row.surveyid);
        setOpenReloadModal(true);
    };

    const handleReloadRowConfirm = async () => {
        if (surveyIdToReload === null) { return; }
        await syncSurveyDatas(surveyIdToReload, initialSurveyReload, selectedSchema || '', t('surveys.modal.successSyncSurvey'), t('surveys.modal.errorSyncSurvey'));
        setSurveyIdToReload(null);
        setOpenReloadModal(false);
    };

    const handleInitialSurveyReload = (initialReload: boolean) => {
        setInitialSurveyReload(initialReload);
    };

    // Table rows only hold the displayed columns: the full survey comes from the fetched list
    const handleUpdateRow = (row: any) => {
        const survey = fetchedSurveys?.find((s: SurveyForConfigurationProps) => s.surveyid === row.surveyid);
        if (survey) {
            setSurveyToEdit(survey);
        }
    };

    const handleCloseEditSurvey = () => {
        setSurveyToEdit(undefined);
        refreshMutate();
    };

    useEffect(() => {
        if (fetchedSurveys) {
            const generatedHeaders: Header[] = [
                { id: 'surveyId', label: 'Id', type: HEADER_TYPE.OPEN, rowKey: 'surveyid', displayFilter: false },
                { id: 'name', label: 'Nom', type: HEADER_TYPE.OPEN, rowKey: 'name', displayFilter: true },
                { id: 'surveyStatus', label: 'Status', type: HEADER_TYPE.CHOICE, rowKey: 'status', values: listToUnique(fetchedSurveys.map((survey: SurveyForConfigurationProps) => SurveyStatusToLabel[survey.status])), displayFilter: true },
                { id: 'clientName', label: 'Client', type: HEADER_TYPE.CHOICE, rowKey: 'clientName', values: listToUnique(fetchedSurveys.map((survey: SurveyForConfigurationProps) => survey.clientName)), displayFilter: true },
                { id: 'context', label: 'Contexte', type: HEADER_TYPE.OPEN, rowKey: 'context', displayFilter: true },
                { id: 'createdby', label: 'Créé par', type: HEADER_TYPE.OPEN, rowKey: 'createdby', displayFilter: true },
                { id: 'createdat', label: 'Créé le', type: HEADER_TYPE.DATE, rowKey: 'createdat', displayFilter: true, filterDate: { date: null, operator: null } },
            ];
            setHeaders(generatedHeaders);
        }
    }, [fetchedSurveys]);

    return (
        <>
            <HeaderSurveysListStyled>
                <SurveysListActionsHeaderStyled>
                    <AddSurveysAction
                        openCreateSurveyModal={openCreateSurveyModal}
                        setOpenCreateSurveyModal={setOpenCreateSurveyModal}
                    />
                </SurveysListActionsHeaderStyled>
            </HeaderSurveysListStyled>
            {(headers.length > 0) && <VirtualizedTable handleDeleteRow={handleDeleteRow} handleReloadRow={handleReloadRow} handleUpdateRow={handleUpdateRow} headers={headers} rows={fetchedSurveys} />}
            {surveyToEdit && (
                <CreateSurvey
                    handleClose={handleCloseEditSurvey}
                    openModal={surveyToEdit !== undefined}
                    surveyToEdit={surveyToEdit}
                />
            )}
            {openDeleteModal && (
                <DeleteContentModal
                    handleClickConfirm={handleDeleteRowConfirm}
                    handleClose={() => setOpenDeleteModal(false)}
                    openModal={openDeleteModal}
                    title={modalTitle}
                />
            )}
            {openReloadModal && (
                <DeleteContentModal
                    checkboxLabel={t('surveys.reloadSurveyCheckbox')}
                    handleCheckbox={handleInitialSurveyReload}
                    handleClickConfirm={handleReloadRowConfirm}
                    handleClose={() => setOpenReloadModal(false)}
                    openModal={openReloadModal}
                    title={reloadModalTitle}
                />
            )}
        </>
    );
};

export default SurveysList;
